// Formal Quote Module (CLAUDE.md) — build a multi-product price quote: pick several products
// (the same product can appear more than once under a different pricing scenario, e.g. "Retail"
// vs "Food Service"), share one gross margin across the whole quote, generate a professional PDF,
// and optionally push the quoted prices straight into the customer's real price list (creating the
// Customer itself first if this was a prospect who didn't exist yet).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import quoteService from '../services/quoteService';
import productService from '../services/productService';
import customerService from '../services/customerService';
import analyticsService from '../services/analyticsService';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmModal from '../components/ConfirmModal';

const today = () => new Date().toISOString().slice(0, 10);
const defaultValidUntil = (quoteDate) => {
  const d = new Date(`${quoteDate}T00:00:00`);
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

let keySeq = 1;
const blankItem = (productId = '') => ({
  key: keySeq++,
  productId,
  scenarioLabel: '',
  ingredientCostPerUnit: '',
  packagingCostPerUnit: '',
  laborCostPerUnit: '',
  marginPercentOverride: '',
  showMarginOverride: false,
});

// Gross margin, not markup — sell = cost / (1 - margin/100), same formula as the Price
// Calculator's own Manufacturer Gross Margin field (AdminProductPage.js's calcHandleMfgGM).
// Clamped to [0, 99.9) — a margin of 100% or more is mathematically undefined, matching that same
// calculator's own `max="99.9"` input bound.
const computeUnitPrice = (item, quoteMarginPercent) => {
  const cost = (parseFloat(item.ingredientCostPerUnit) || 0)
    + (parseFloat(item.packagingCostPerUnit) || 0)
    + (parseFloat(item.laborCostPerUnit) || 0);
  const margin = item.marginPercentOverride !== '' && item.marginPercentOverride != null
    ? parseFloat(item.marginPercentOverride) : (parseFloat(quoteMarginPercent) || 0);
  const clampedMargin = Math.min(Math.max(margin, 0), 99.9);
  return cost / (1 - clampedMargin / 100);
};

export default function QuoteBuilderPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [products, setProducts] = useState([]);
  const [recipeCosts, setRecipeCosts] = useState({});
  const [customers, setCustomers] = useState([]);

  const [customerId, setCustomerId] = useState('');
  const [prospectName, setProspectName] = useState('');
  const [prospectEmail, setProspectEmail] = useState('');
  const [prospectPhone, setProspectPhone] = useState('');
  const [marginPercent, setMarginPercent] = useState('30');
  const [quoteDate, setQuoteDate] = useState(today());
  const [validUntil, setValidUntil] = useState(defaultValidUntil(today()));
  const [validUntilTouched, setValidUntilTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([blankItem()]);

  const [pushedToPriceListAt, setPushedToPriceListAt] = useState(null);
  const [pdfGeneratedAt, setPdfGeneratedAt] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);

  const [laborDays, setLaborDays] = useState(null);
  const [laborLoading, setLaborLoading] = useState(false);
  const [sharedLaborCost, setSharedLaborCost] = useState('');

  const isLocked = !!pushedToPriceListAt;

  const load = useCallback(async () => {
    const [productList, recipeCostRows, customerList] = await Promise.all([
      productService.getProducts({ includeInactive: false }),
      productService.getRecipeCosts(),
      customerService.getCustomers(),
    ]);
    setProducts(productList);
    const costMap = {};
    for (const r of recipeCostRows) costMap[r.productId] = r.estimatedCostPerUnit;
    setRecipeCosts(costMap);
    setCustomers(customerList);

    if (!isNew) {
      const quote = await quoteService.getQuote(id);
      setCustomerId(quote.customerId ? String(quote.customerId) : '');
      setProspectName(quote.prospectName || '');
      setProspectEmail(quote.prospectEmail || '');
      setProspectPhone(quote.prospectPhone || '');
      setMarginPercent(String(quote.marginPercent));
      setQuoteDate(quote.quoteDate);
      setValidUntil(quote.validUntil);
      setValidUntilTouched(true);
      setNotes(quote.notes || '');
      setPushedToPriceListAt(quote.pushedToPriceListAt);
      setPdfGeneratedAt(quote.pdfGeneratedAt);
      setItems((quote.Items || []).map(i => ({
        key: keySeq++,
        productId: String(i.productId),
        scenarioLabel: i.scenarioLabel || '',
        ingredientCostPerUnit: String(i.ingredientCostPerUnit),
        packagingCostPerUnit: String(i.packagingCostPerUnit),
        laborCostPerUnit: String(i.laborCostPerUnit),
        marginPercentOverride: i.marginPercentOverride != null ? String(i.marginPercentOverride) : '',
        showMarginOverride: i.marginPercentOverride != null,
      })));
    }
  }, [id, isNew]);

  useEffect(() => {
    setLoading(true);
    load().catch(() => setError('Failed to load.')).finally(() => setLoading(false));
  }, [load]);

  const productOptions = useMemo(() => products.map(p => ({ value: p.id, label: p.name })), [products]);
  const customerOptions = useMemo(() => customers.map(c => ({ value: c.id, label: c.name })), [customers]);

  const setItemField = (key, field, value) => {
    setItems(prev => prev.map(it => (it.key === key ? { ...it, [field]: value } : it)));
  };

  const setItemProduct = (key, productId) => {
    setItems(prev => prev.map(it => (it.key === key ? {
      ...it,
      productId,
      ingredientCostPerUnit: recipeCosts[productId] != null ? String(recipeCosts[productId]) : it.ingredientCostPerUnit,
    } : it)));
  };

  const addItem = () => setItems(prev => [...prev, blankItem()]);
  const removeItem = (key) => setItems(prev => prev.filter(it => it.key !== key));
  const duplicateAsScenario = (key) => {
    setItems(prev => {
      const src = prev.find(it => it.key === key);
      if (!src) return prev;
      const idx = prev.findIndex(it => it.key === key);
      const copy = { ...src, key: keySeq++, scenarioLabel: '' };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const fetchSharedLaborCost = async (days) => {
    setLaborDays(days);
    setLaborLoading(true);
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      const fmt = d => d.toISOString().slice(0, 10);
      const data = await analyticsService.getProductionSummary({ startDate: fmt(start), endDate: fmt(end) });
      const val = data?.kpis?.laborCostPerUnit;
      setSharedLaborCost(val != null ? String(Number(val).toFixed(4)) : '');
    } catch {
      setSharedLaborCost('');
    } finally {
      setLaborLoading(false);
    }
  };

  const handleQuoteDateChange = (value) => {
    setQuoteDate(value);
    if (!validUntilTouched) setValidUntil(defaultValidUntil(value));
  };

  const validate = () => {
    if (!customerId && !prospectName.trim()) return 'Select a customer or enter a prospect name.';
    if (items.length === 0) return 'At least one line item is required.';
    for (const it of items) {
      if (!it.productId) return 'Every line needs a product selected.';
    }
    return null;
  };

  const buildPayload = () => ({
    customerId: customerId || null,
    prospectName: customerId ? null : prospectName.trim(),
    prospectEmail: customerId ? null : (prospectEmail || null),
    prospectPhone: customerId ? null : (prospectPhone || null),
    marginPercent: Number(marginPercent) || 0,
    quoteDate,
    validUntil,
    notes: notes.trim() || null,
    items: items.map(it => ({
      productId: Number(it.productId),
      scenarioLabel: it.scenarioLabel.trim() || null,
      ingredientCostPerUnit: Number(it.ingredientCostPerUnit) || 0,
      packagingCostPerUnit: Number(it.packagingCostPerUnit) || 0,
      laborCostPerUnit: Number(it.laborCostPerUnit) || 0,
      marginPercentOverride: it.showMarginOverride && it.marginPercentOverride !== '' ? Number(it.marginPercentOverride) : null,
    })),
  });

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await quoteService.createQuote(payload);
        navigate(`/quotes/${created.id}`, { replace: true });
      } else {
        await quoteService.updateQuote(id, payload);
        await load();
      }
    } catch (err2) {
      setError(err2.response?.data?.message || 'Failed to save quote.');
    } finally {
      setSaving(false);
    }
  };

  const buildQuoteFilename = () => {
    const d = new Date(`${quoteDate}T00:00:00`);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const namePart = slugify(selectedCustomerName) || `quote${id}`;
    return `Quote_${namePart}_${mm}${dd}${yy}.pdf`;
  };

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    setError('');
    try {
      const blob = await quoteService.generatePdf(id);
      setPdfGeneratedAt(new Date().toISOString());
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = buildQuoteFilename();
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // With responseType:'blob', an error body also arrives as a Blob, not parsed JSON.
      if (err.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text());
          setError(parsed.message || 'Failed to generate PDF.');
        } catch {
          setError('Failed to generate PDF.');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to generate PDF.');
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handlePush = async () => {
    setPushing(true);
    setError('');
    try {
      await quoteService.pushToPriceList(id);
      setShowPushConfirm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to push to price list.');
    } finally {
      setPushing(false);
    }
  };

  const total = items.reduce((s, it) => s + computeUnitPrice(it, marginPercent), 0);
  const selectedCustomerName = customerId ? customers.find(c => String(c.id) === String(customerId))?.name : prospectName;

  if (loading) {
    return <div className="container mt-4 text-center text-muted py-5"><div className="spinner-border spinner-border-sm me-2" />Loading…</div>;
  }

  const CARD_STYLE = (color) => ({ border: '1px solid #e9ecef', borderTopWidth: 3, borderTopStyle: 'solid', borderTopColor: color });
  const SECTION_HEADER_STYLE = { fontSize: 11, letterSpacing: 1, fontWeight: 700 };

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 pb-3 gap-2" style={{ borderBottom: '2px solid #f1f3f5' }}>
        <div>
          <h4 className="mb-1">📄 {isNew ? 'New Quote' : `Quote #${id}`}</h4>
          {isLocked && <span className="badge bg-success">Pushed to Price List</span>}
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/quotes')}>← Back to Quotes</button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card mb-3" style={CARD_STYLE('#0d6efd')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>🏷️ Recipient</div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Existing Customer</label>
              <SearchableSelect
                options={customerOptions}
                value={customerId}
                onChange={v => { setCustomerId(v || ''); if (v) { setProspectName(''); setProspectEmail(''); setProspectPhone(''); } }}
                placeholder="Select if they're already a customer…"
                size="sm"
                disabled={isLocked}
              />
            </div>
          </div>
          {!customerId && (
            <div className="row g-3 mt-1">
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Prospect Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={prospectName} onChange={e => setProspectName(e.target.value)} placeholder="e.g. Fresh Mart Deli" disabled={isLocked} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Email</label>
                <input className="form-control form-control-sm" value={prospectEmail} onChange={e => setProspectEmail(e.target.value)} disabled={isLocked} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Phone</label>
                <input className="form-control form-control-sm" value={prospectPhone} onChange={e => setProspectPhone(e.target.value)} disabled={isLocked} />
              </div>
              <div className="col-12">
                <div className="form-text" style={{ fontSize: 11 }}>
                  Not in the system yet — pushing this quote to a price list will create a new customer record with this name.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#6f42c1')}>
        <div className="card-body">
          <div className="text-uppercase text-muted mb-3" style={SECTION_HEADER_STYLE}>⚗️ Quote Terms</div>
          <div className="row g-3">
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Gross Margin % <span className="text-muted">(applies to all lines)</span></label>
              <div className="input-group input-group-sm">
                <input type="number" min="0" max="99.9" step="0.1" className="form-control" value={marginPercent} onChange={e => setMarginPercent(e.target.value)} disabled={isLocked} />
                <span className="input-group-text">%</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Quote Date</label>
              <input type="date" className="form-control form-control-sm" value={quoteDate} onChange={e => handleQuoteDateChange(e.target.value)} disabled={isLocked} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Valid Until</label>
              <input type="date" className="form-control form-control-sm" value={validUntil} onChange={e => { setValidUntil(e.target.value); setValidUntilTouched(true); }} disabled={isLocked} />
            </div>
          </div>

          <div className="mt-3">
            <label className="form-label small fw-semibold">Shared labor cost helper <span className="text-muted">(company-wide average — copy into any line below)</span></label>
            <div className="btn-group btn-group-sm mb-1" role="group">
              {[30, 60, 90, 365].map(d => (
                <button key={d} type="button" className={`btn btn-outline-primary${laborDays === d ? ' active' : ''}`}
                  onClick={() => fetchSharedLaborCost(d)} disabled={isLocked}>
                  Last {d}d
                </button>
              ))}
            </div>
            {laborLoading && <span className="text-muted ms-2" style={{ fontSize: 12 }}>Loading…</span>}
            {!laborLoading && sharedLaborCost !== '' && (
              <span className="ms-2" style={{ fontSize: 13 }}>${Number(sharedLaborCost).toFixed(4)}/unit (last {laborDays}d)</span>
            )}
          </div>

          <div className="mt-3">
            <label className="form-label small fw-semibold">Notes <span className="text-muted">(printed on the PDF)</span></label>
            <textarea className="form-control form-control-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} disabled={isLocked} />
          </div>
        </div>
      </div>

      <div className="card mb-3" style={CARD_STYLE('#198754')}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="text-uppercase text-muted" style={SECTION_HEADER_STYLE}>🧾 Line Items</div>
            {!isLocked && <button className="btn btn-sm btn-outline-primary" onClick={addItem}>+ Add Product</button>}
          </div>

          {items.map(it => {
            const unitPrice = computeUnitPrice(it, marginPercent);
            return (
              <div key={it.key} className="border rounded p-2 mb-2">
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label small mb-1">Product</label>
                    <SearchableSelect options={productOptions} value={it.productId} onChange={v => setItemProduct(it.key, v)} placeholder="Select product…" size="sm" disabled={isLocked} />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Scenario</label>
                    <input className="form-control form-control-sm" value={it.scenarioLabel} onChange={e => setItemField(it.key, 'scenarioLabel', e.target.value)} placeholder="e.g. Retail" disabled={isLocked} />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Ingredient $</label>
                    <input type="number" step="0.0001" className="form-control form-control-sm" value={it.ingredientCostPerUnit} onChange={e => setItemField(it.key, 'ingredientCostPerUnit', e.target.value)} disabled={isLocked} />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Packaging $</label>
                    <input type="number" step="0.0001" className="form-control form-control-sm" value={it.packagingCostPerUnit} onChange={e => setItemField(it.key, 'packagingCostPerUnit', e.target.value)} disabled={isLocked} />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label small mb-1 d-flex justify-content-between">
                      Labor $
                      {sharedLaborCost !== '' && !isLocked && (
                        <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 10 }} onClick={() => setItemField(it.key, 'laborCostPerUnit', sharedLaborCost)}>use ${Number(sharedLaborCost).toFixed(2)}</button>
                      )}
                    </label>
                    <input type="number" step="0.0001" className="form-control form-control-sm" value={it.laborCostPerUnit} onChange={e => setItemField(it.key, 'laborCostPerUnit', e.target.value)} disabled={isLocked} />
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div>
                    {!it.showMarginOverride ? (
                      !isLocked && <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={() => setItemField(it.key, 'showMarginOverride', true)}>Override margin for this line</button>
                    ) : (
                      <div className="d-flex align-items-center gap-1">
                        <span className="text-muted" style={{ fontSize: 11 }}>Margin override:</span>
                        <input type="number" min="0" max="99.9" step="0.1" className="form-control form-control-sm" style={{ width: 80 }} value={it.marginPercentOverride} onChange={e => setItemField(it.key, 'marginPercentOverride', e.target.value)} disabled={isLocked} />
                        <span style={{ fontSize: 11 }}>%</span>
                        {!isLocked && <button type="button" className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: 11 }} onClick={() => { setItemField(it.key, 'showMarginOverride', false); setItemField(it.key, 'marginPercentOverride', ''); }}>clear</button>}
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <strong style={{ fontSize: 15 }}>${unitPrice.toFixed(2)}</strong>
                    {!isLocked && (
                      <>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => duplicateAsScenario(it.key)}>+ Duplicate as another scenario</button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(it.key)} disabled={items.length <= 1}>Remove</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="d-flex justify-content-end mt-2">
            <div style={{ fontSize: 16 }}><span className="text-muted">Quote total: </span><strong>${total.toFixed(2)}</strong></div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2">
        {!isLocked && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isNew ? 'Save Draft' : 'Save Changes')}
          </button>
        )}
        {!isNew && (
          <button className="btn btn-outline-dark" onClick={handleGeneratePdf} disabled={generatingPdf}>
            {generatingPdf ? 'Generating…' : (pdfGeneratedAt ? '🖨️ Regenerate & Download PDF' : '🖨️ Generate PDF')}
          </button>
        )}
        {!isNew && !isLocked && (
          <button className="btn btn-success" onClick={() => setShowPushConfirm(true)}>
            📋 Push to Customer Price List
          </button>
        )}
      </div>

      <ConfirmModal
        show={showPushConfirm}
        title="Push to Customer Price List"
        message={customerId
          ? `This will add ${items.length} priced line item(s) to ${selectedCustomerName}'s price list. This cannot be undone.`
          : `This will create a new customer named "${selectedCustomerName}" and add ${items.length} priced line item(s) to their price list. This cannot be undone.`}
        confirmLabel={pushing ? 'Pushing…' : 'Push to Price List'}
        variant="success"
        onConfirm={handlePush}
        onCancel={() => setShowPushConfirm(false)}
      />
    </div>
  );
}
